import {
  Color,
  DesaturateFilter,
  Filter,
  ImageScaleView,
  LinearAddFilter,
  View,
} from '@play-co/timestep-core/ui';
import { FeatureButton } from 'src/game/components/FeatureButton';
import sounds from 'src/lib/sounds';
import { getApplication } from 'src/lib/utils';
import { getBurstPool } from '../animation/effects';
import gsap from 'gsap';

const assets = {
  icon: 'assets/ui/accessories/icons/lock_icon.png',
};

export type HomeButtonLockedOverlayOpts = {
  button: FeatureButton<any>;
  iconOffsetX?: number;
  iconOffsetY?: number;
  locked?: boolean;
};

// Desaturate filter can be shared across instances.
let desaturateFilter: DesaturateFilter;

export class HomeButtonLockedOverlay extends View {
  // Linear Add filter is to be manipulated per instance basis.
  private linearAddFilter: LinearAddFilter = new LinearAddFilter('#ffffff');
  private readonly button: FeatureButton;
  private readonly icon: ImageScaleView;
  private readonly baseIconPosition: { x: number; y: number };
  private _locked = false;

  public get locked() {
    return this._locked;
  }

  constructor(opts: HomeButtonLockedOverlayOpts) {
    const { button, iconOffsetX = 0, iconOffsetY = 0, locked = false } = opts;

    super({
      superview: button,
      width: button.style.width || 144,
      height: button.style.height || 160,
    });

    // Initialize desaturate filter, if not already
    if (!desaturateFilter) {
      desaturateFilter = new DesaturateFilter(0.75);
    }

    this.button = button;

    // Create locked icon
    this.icon = new ImageScaleView({
      superview: this,
      image: assets.icon,
      width: 50,
      height: 59,
      x: this.style.width - 26 + iconOffsetX,
      y: 40 + iconOffsetY,
      centerOnOrigin: true,
      zIndex: 100,
    });
    this.baseIconPosition = { x: this.icon.style.x, y: this.icon.style.y };

    // Set initial locked state
    if (locked) this.lock();
    else this.unlock(true);
  }

  /**
   * Locks the interactivity of and apply the desaturate filter to the bound button,
   * as well as showing the locked overlay on top.
   */
  public lock() {
    this._locked = true;
    this.applyFilterToButton(desaturateFilter);
    this.button.updateOpts({ blockEvents: true });
    this.icon.updateOpts({
      ...this.baseIconPosition,
      opacity: 1,
      scale: 1,
    });
    this.updateOpts({ visible: true });
  }

  /**
   * Unlocks the interactivity of and remove the filter from the bound button,
   * as well as hiding the locked overlay.
   */
  public async unlock(instant = false) {
    this._locked = false;

    if (!instant) {
      this.setLinearAddValue(1);

      const animator = { value: 1 };

      await gsap
        .timeline()
        // Move lock icon to the center
        .to(this.icon.style, {
          duration: 0.7,
          x: this.style.width * 0.5,
          y: this.style.height * 0.5,
          scale: 1.5,
          ease: 'quad.inOut',
        })
        // Shake lock icon
        .to(this.icon.style, {
          duration: 0.05,
          x: this.style.width * 0.5 + 7,
          repeat: 5,
          yoyo: true,
          ease: 'quad.inOut',
        })
        .to(
          this.icon.style,
          {
            duration: 0.035,
            y: this.style.height * 0.5 - 7,
            repeat: 8,
            yoyo: true,
            ease: 'quad.inOut',
          },
          '<',
        )
        // Enlarge fade out lock icon
        .to(this.icon.style, {
          duration: 0.5,
          opacity: 0,
          scale: 2.75,
          ease: 'quad.out',
        })
        // Apply linear add filter and value out
        .to(
          animator,
          {
            duration: 0.75,
            value: 0,
            ease: 'quad.in',
            onStart: () => {
              this.applyFilterToButton(this.linearAddFilter);
              this.playBurstVfx();
            },
            onUpdate: () => this.setLinearAddValue(animator.value),
            overwrite: true,
          },
          '<',
        );
    }

    this.button.updateOpts({ blockEvents: false });
    this.updateOpts({ visible: false });
    this.removeFilterFromButton();
  }

  private playBurstVfx() {
    // Play SFX
    sounds.playSound('gachaItemDrop', 0.3);

    const root = getApplication().getRootView();
    const bigBurst = getBurstPool().obtainView();
    const { x, y } = this.getPosition(root);

    bigBurst.updateOpts({
      superview: root,
      x: x + this.style.width * 0.5,
      y: y + this.style.height * 0.5,
      zIndex: 10001,
      scale: 1.5,
    });

    // Play a big burst VFX
    bigBurst.play('eventicon_burst', () => {
      bigBurst.removeFromSuperview();
      getBurstPool().releaseView(bigBurst);
    });
  }

  private applyFilterToButton(filter: Filter) {
    this.button
      .getElementsToApplyFilter()
      .forEach((element) => element?.setFilter(filter));
  }

  private removeFilterFromButton() {
    this.button
      .getElementsToApplyFilter()
      .forEach((element) => element?.removeFilter());
  }

  private setLinearAddValue(value: number) {
    this.linearAddFilter.update(Color.parse(`rgba(255, 255, 255, ${value})`));
  }
}
